import React, { useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, message } from 'antd';
import { createRecommendationTemplate } from '../../services/api/odinApi';

const { TextArea } = Input;

const PLATFORM_OPTIONS = [
  { value: 'cipp', label: 'Microsoft 365' },
  { value: 'sophos', label: 'Sophos' },
  { value: 'acronis', label: 'Acronis' },
  { value: 'autotask', label: 'Autotask' },
  { value: 'scalepad', label: 'ScalePad' },
  { value: 'general', label: 'General' },
];

const ICON_OPTIONS = [
  '🛡️', '📧', '💾', '🔒', '📊', '🎫', '⚙️', '🚀', '📋', '🔍',
  '💡', '⚠️', '✅', '🔧', '📈',
];

const TemplateUploadModal = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Parse variables JSON if provided
      let variables = {};
      if (values.variablesJson) {
        try {
          variables = JSON.parse(values.variablesJson);
        } catch {
          message.error('Invalid JSON in variables field');
          setSubmitting(false);
          return;
        }
      }

      const result = await createRecommendationTemplate({
        platform: values.platform,
        title: values.title,
        description: values.description,
        html_content: values.html_content,
        icon: values.icon,
        display_order: values.display_order || 0,
        variables,
      });

      if (result?.success) {
        message.success('Template created');
        form.resetFields();
        onCreated?.(result.template);
        onClose();
      } else {
        message.error(result?.error || 'Failed to create template');
      }
    } catch (err) {
      if (err.errorFields) return; // form validation error
      console.error('Create template failed:', err);
      message.error('Failed to create template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create Recommendation Template"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Create Template"
      confirmLoading={submitting}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ platform: 'general', display_order: 0, icon: '📋' }}
      >
        <Form.Item
          name="platform"
          label="Platform"
          rules={[{ required: true, message: 'Select a platform' }]}
        >
          <Select options={PLATFORM_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Enter a title' }]}
        >
          <Input placeholder="e.g., Enable Multi-Factor Authentication" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input placeholder="Brief description of the recommendation" />
        </Form.Item>

        <Form.Item name="icon" label="Icon">
          <Select>
            {ICON_OPTIONS.map((icon) => (
              <Select.Option key={icon} value={icon}>
                {icon}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="html_content"
          label="HTML Content"
          rules={[{ required: true, message: 'Enter the HTML content' }]}
          extra="HTML for the recommendation card. Supports Handlebars {{variables}}."
        >
          <TextArea
            rows={6}
            placeholder='<div class="recommendation-card">
  <div class="rec-header">
    <span class="rec-icon">🛡️</span>
    <strong>Enable MFA</strong>
  </div>
  <div class="rec-body">
    <p>Protect accounts with multi-factor authentication.</p>
  </div>
</div>'
          />
        </Form.Item>

        <Form.Item
          name="variablesJson"
          label="Variables (JSON)"
          extra='Optional default values, e.g., {"cost": "$500/mo", "users": "10"}'
        >
          <TextArea rows={2} placeholder='{"cost": "$500/mo"}' />
        </Form.Item>

        <Form.Item name="display_order" label="Display Order">
          <InputNumber min={0} max={999} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateUploadModal;
